import { useCallback, useEffect, useRef, useState } from "react";
import { TrainingAPI } from "@api/TrainingAPI";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import type { Attachment } from "../types";

const trainingAPI = new TrainingAPI();

const ACCEPTED_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_SIZE_BYTES = 5 * 1024 * 1024;
export const MAX_IMAGES = 5;
export const MAX_SIZE_MB = MAX_SIZE_BYTES / (1024 * 1024);

// Monotonic id so attachments are tracked by identity, never by array index.
let attachmentSeq = 0;
const nextAttachmentId = () => `att-${attachmentSeq++}`;

// `disabled` (a reply is running) blocks staging here, so every path — drop, paste, attach
// button, screenshot — shows the same error from one place.
export function useImageAttachments(conversationEpoch: number, disabled: boolean) {
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const attachmentsRef = useRef<Attachment[]>([]);
    attachmentsRef.current = attachments;

    // No draft/per-conversation staging concept: switching conversations discards any unsent
    // images rather than risking them landing in the wrong conversation. Keyed on
    // conversationEpoch (bumped only by an actual switch/new-chat), not conversationId.
    useEffect(() => {
        attachmentsRef.current.forEach(a => URL.revokeObjectURL(a.previewUrl));
        attachmentsRef.current = []; // now, not at the next render: a failing upload must find nothing
        setAttachments([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationEpoch]);

    // Unmount safety net for any preview URLs the epoch-reset effect above didn't already catch.
    useEffect(() => {
        return () => {
            attachmentsRef.current.forEach(a => URL.revokeObjectURL(a.previewUrl));
            // An upload still in flight can fail after the user leaves; with the list empty its
            // catch finds no target and shows no toast on the next page.
            attachmentsRef.current = [];
        };
    }, []);

    // One request per image; update the matching attachment by id (not position).
    const uploadOne = useCallback(async (id: string, file: File) => {
        try {
            const ref = await trainingAPI.uploadChatbotImage(file);
            setAttachments(prev => prev.map(a => (a.id === id ? { ...a, ref, status: "ready" } : a)));
        } catch {
            // Drop the failed image (revoke its preview) and alert; the other images still send.
            // No target: the user removed the image (or left the chat or switched conversation)
            // while it uploaded, so there is nothing to report.
            const target = attachmentsRef.current.find(a => a.id === id);
            if (!target) return;
            URL.revokeObjectURL(target.previewUrl);
            setAttachments(prev => prev.filter(a => a.id !== id));
            message.error("An image failed to upload. Please try again.");
        }
    }, []);

    const stageFiles = useCallback((files: File[]) => {
        if (disabled) { message.error("Wait for the response to finish before attaching images."); return; }
        const imageFiles = files.filter(f => f.type.startsWith("image/"));
        if (files.some(f => !f.type.startsWith("image/"))) message.error("Only images can be attached.");

        // Per-file type/size checks still reject individually with their own message.
        const valid: File[] = [];
        for (const f of imageFiles) {
            if (!ACCEPTED_TYPES.has(f.type)) { message.error(`${f.name}: only PNG, JPEG, or WEBP.`); continue; }
            if (f.size > MAX_SIZE_BYTES) { message.error(`${f.name}: exceeds 5 MB.`); continue; }
            valid.push(f);
        }
        if (valid.length === 0) return;

        // Cap check is all-or-nothing: a batch that doesn't fully fit stages nothing.
        const remaining = MAX_IMAGES - attachmentsRef.current.length;
        if (valid.length > remaining) {
            message.error(
                remaining > 0
                    ? `Can only add ${remaining} more image${remaining === 1 ? "" : "s"} (max ${MAX_IMAGES}).`
                    : `Max ${MAX_IMAGES} images per message.`
            );
            return;
        }

        const batch: Attachment[] = valid.map(f => ({
            id: nextAttachmentId(),
            file: f,
            previewUrl: URL.createObjectURL(f),
            ref: null,
            fileName: f.name,
            status: "uploading",
        }));
        setAttachments(prev => [...prev, ...batch]);
        batch.forEach(a => { void uploadOne(a.id, a.file); });
    }, [uploadOne, disabled]);

    const handleRemove = useCallback((id: string) => {
        const target = attachmentsRef.current.find(a => a.id === id);
        if (target) URL.revokeObjectURL(target.previewUrl); // unsent → never primed → safe
        // Update the ref now, not at the next render: an upload that fails in the same tick
        // must already see the image as removed.
        attachmentsRef.current = attachmentsRef.current.filter(a => a.id !== id);
        setAttachments(prev => prev.filter(a => a.id !== id));
    }, []);

    return { attachments, setAttachments, attachmentsRef, stageFiles, handleRemove };
}
